import { setCaret } from "./macro-caret-utils"

export function findContainingChip(node, boundary) {
    let current = node.nodeType === Node.TEXT_NODE ? node.parentNode : node
    while (current && current !== boundary) {
        if (current.nodeType === Node.ELEMENT_NODE && current.classList?.contains("macro-chip")) return current
        current = current.parentNode
    }
    return null
}

export function findAdjacentMacroChip(range, direction) {
    const boundary = getCaretBoundary(range, direction)
    if (!boundary) return null
    return findChipAtBoundary(boundary.node, boundary.direction)
}

export function getCaretBoundary(range, direction) {
    const { startContainer, startOffset } = range

    if (startContainer.nodeType === Node.TEXT_NODE) {
        const text = startContainer.nodeValue ?? ""
        if (direction === "backward" && startOffset !== 0) return null
        if (direction === "forward" && startOffset !== text.length) return null
        return { node: startContainer, direction }
    }

    const childNodes = startContainer.childNodes ?? []
    const adjacentNode =
        direction === "backward" ? childNodes[startOffset - 1] ?? null : childNodes[startOffset] ?? null

    if (adjacentNode) {
        return { node: adjacentNode, direction: direction === "backward" ? "inside-backward" : "inside-forward" }
    }

    return { node: startContainer, direction }
}

export function findChipAtBoundary(node, direction) {
    if (!node) return null

    if (direction === "inside-backward") {
        return resolveInlineBoundaryNode(getLastInlineBoundaryNode(node))
    }
    if (direction === "inside-forward") {
        return resolveInlineBoundaryNode(getFirstInlineBoundaryNode(node))
    }

    let current = node
    let currentDirection = direction

    for (;;) {
        const sibling = currentDirection === "backward" ? current.previousSibling : current.nextSibling
        const candidate = resolveSiblingCandidate(sibling, currentDirection)
        if (candidate !== undefined) return candidate

        const parent = current.parentNode
        if (!parent || isBlockBoundary(parent)) return null
        current = parent
        currentDirection = direction
    }
}

export function resolveSiblingCandidate(sibling, direction) {
    let current = sibling

    while (current) {
        if (current.nodeType === Node.TEXT_NODE) {
            if ((current.nodeValue ?? "").trim() !== "") return null
            current = direction === "backward" ? current.previousSibling : current.nextSibling
            continue
        }

        return resolveInlineBoundaryNode(
            direction === "backward" ? getLastInlineBoundaryNode(current) : getFirstInlineBoundaryNode(current)
        )
    }

    return undefined
}

export function resolveInlineBoundaryNode(node) {
    if (!node) return null
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.nodeValue ?? "").trim() ? null : null
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null
    if (node.tagName === "BR" || isBlockBoundary(node)) return null
    if (node.classList?.contains("macro-chip")) return node

    return null
}

export function getLastInlineBoundaryNode(node) {
    let current = node
    while (
        current?.nodeType === Node.ELEMENT_NODE &&
        current.lastChild &&
        !isBlockBoundary(current) &&
        current.tagName !== "BR"
    ) {
        if (current.classList?.contains("macro-chip")) break
        current = current.lastChild
    }
    return current
}

export function getFirstInlineBoundaryNode(node) {
    let current = node
    while (
        current?.nodeType === Node.ELEMENT_NODE &&
        current.firstChild &&
        !isBlockBoundary(current) &&
        current.tagName !== "BR"
    ) {
        if (current.classList?.contains("macro-chip")) break
        current = current.firstChild
    }
    return current
}

export function isBlockBoundary(node) {
    if (node?.nodeType !== Node.ELEMENT_NODE) return false
    return /^(P|DIV|LI|UL|OL|TABLE|TBODY|TR|TD|TH|BLOCKQUOTE|H[1-6])$/.test(node.tagName)
}

export function removeMacroChip(chip, direction) {
    const parent = chip?.parentNode
    if (!parent) return

    const marker = document.createTextNode("")
    if (direction === "backward") {
        parent.insertBefore(marker, chip.nextSibling)
    } else {
        parent.insertBefore(marker, chip)
    }

    const spacer = findChipSpacer(chip)
    chip.remove()
    spacer?.remove()
    setCaret(marker, 0)
}

export function findChipSpacer(chip) {
    const next = chip?.nextSibling
    if (next?.nodeType !== Node.TEXT_NODE) return null
    if (!/^\s+/.test(next.nodeValue ?? "")) return null

    if ((next.nodeValue ?? "").trim() !== "") {
        next.nodeValue = next.nodeValue.replace(/^\s+/, "")
        return null
    }

    return next
}

/**
 * Creates a chip DOM element from trusted chip HTML (generated by buildChipHtml).
 * Direct DOM insertion via range.insertNode() avoids paragraph-merging quirks
 * that document.execCommand("insertHTML") causes in contenteditable.
 */
export function createChipElement(chipHtml) {
    const template = document.createElement("template")
    template.innerHTML = chipHtml
    const fragment = document.createDocumentFragment()
    while (template.content.firstChild) {
        fragment.appendChild(template.content.firstChild)
    }
    return fragment
}
